import { getConfig } from "@/config";
import { toRouteType } from "./types";
import NProgress from "@/utils/progress";
import { findIndex } from "lodash-unified";
import { sessionKey, type DataInfo } from "@/utils/auth";
import { useMultiTagsStoreHook } from "@/store/modules/multiTags";
import { usePermissionStoreHook } from "@/store/modules/permission";
import {
  Router,
  createRouter,
  RouteRecordRaw,
  RouteComponent
} from "vue-router";
import {
  ascending,
  initRouter,
  isOneOfArray,
  getHistoryMode,
  findRouteByPath,
  handleAliveRoute,
  formatTwoStageRoutes,
  formatFlatteningRoutes,
  findRouteByName
} from "./utils";
import {
  buildHierarchyTree,
  openLink,
  isUrl,
  storageSession
} from "@pureadmin/utils";
import { RouteConfigs } from "@/layout/types";

import homeRouter from "./modules/home";
import errorRouter from "./modules/error";
import remainingRouter from "./modules/remaining";

/** 原始静态路由（未做任何处理） */
const routes = [homeRouter, errorRouter];

/** 导出处理后的静态路由（三级及以上的路由全部拍成二级） */
export const constantRoutes: Array<RouteRecordRaw> = formatTwoStageRoutes(
  formatFlatteningRoutes(buildHierarchyTree(ascending(routes)))
);

/** 用于渲染菜单，保持原始层级 */
export const constantMenus: Array<RouteComponent> = ascending(routes).concat(
  ...remainingRouter
);

/** 不参与菜单的路由 */
export const remainingPaths = Object.keys(remainingRouter).map((v: string) => {
  return (remainingRouter as any)[v].path;
});

/** 创建路由实例 */
export const router: Router = createRouter({
  history: getHistoryMode(),
  routes: constantRoutes.concat(...(remainingRouter as any)),
  strict: true,
  scrollBehavior(to, from, savedPosition) {
    return new Promise(resolve => {
      if (savedPosition) {
        return savedPosition;
      } else {
        if (from.meta.saveSrollTop) {
          const top: number =
            document.documentElement.scrollTop || document.body.scrollTop;
          resolve({ left: 0, top });
        }
      }
    });
  }
});

/** 重置路由 */
export function resetRouter() {
  router.getRoutes().forEach(route => {
    const { name, meta } = route;
    if (name && router.hasRoute(name) && meta?.backstage) {
      router.removeRoute(name);
      router.options.routes = formatTwoStageRoutes(
        formatFlatteningRoutes(buildHierarchyTree(ascending(routes)))
      );
    }
  });
  usePermissionStoreHook().clearAllCachePage();
}

/** 路由白名单 */
const whiteList = [
  "/login",
  "/register",
  "/setPassword",
  "/outResult",
  "/fgStep1",
  "/fgStep2",
  "/fgStep3"
];

/** 根据当前path，添加顶部标签 */
export const handleAddTagsByPath = (to: toRouteType) => {
  const { path, query } = to;
  const index = findIndex(remainingRouter, v => {
    return v.path == path;
  });
  const routes: any =
    index === -1 ? router.options.routes[0].children : router.options.routes;
  let route = findRouteByPath(path, routes);
  // 包含matchedPath的路由，需要匹配到父级的path，来显示tag
  if (route?.meta?.matchedPath) {
    const routeByName = query?.cname
      ? findRouteByName(query?.cname, routes)
      : null;
    console.log("routeByName", routeByName);

    route = routeByName
      ? findRouteByPath((routeByName as any).meta.matchedPath, routes)
      : findRouteByPath(route.meta.matchedPath, routes);
  }
  console.log(route);
  // query、params模式路由传参数的标签页不在此处处理
  if (route && route.meta?.title) {
    useMultiTagsStoreHook().handleTags("push", {
      path: route.path,
      name: route.name,
      meta: route.meta
    });
    console.log("根据path添加的tag", useMultiTagsStoreHook().multiTags);
  }
};

/** 判断路由列表中是否包含某个name */
export const isHasNameByWholeRouterList = (
  routerArr: Array<RouteConfigs>,
  name: string
) => {
  for (let i = 0; i < routerArr.length; i++) {
    const router = routerArr[i];

    if (router.name === name) return true;
    if (router.children?.length) {
      const res = isHasNameByWholeRouterList(router.children, name);
      if (res) return true;
    }
  }
  return false;
};

router.beforeEach((to: toRouteType, _from, next) => {
  if (to.meta?.keepAlive) {
    const newMatched = to.matched;
    handleAliveRoute(newMatched, "add");
    // 页面整体刷新和点击标签页刷新
    if (_from.name === undefined || _from.name === "Redirect") {
      handleAliveRoute(newMatched);
    }
  }
  const userInfo = storageSession.getItem<DataInfo>(sessionKey);
  NProgress.start();
  const externalLink = isUrl(to?.name as string);
  if (!externalLink) {
    to.matched.some(item => {
      if (!item.meta.title) return "";
      const Title = getConfig().Title;
      if (Title) document.title = `${item.meta.title} | ${Title}`;
      else document.title = item.meta.title as string;
    });
  }
  /** 如果已经登录并存在登录信息后不能跳转到路由白名单，而是继续保持在当前页面 */
  function toCorrectRoute() {
    whiteList.includes(to.fullPath) ? next(_from.fullPath) : next();
  }
  if (userInfo?.userToken) {
    // 无权限跳转403页面
    if (to.meta?.roles && !isOneOfArray(to.meta?.roles, userInfo?.roles)) {
      next({ path: "/error/403" });
    }
    if (_from?.name) {
      if (to.path === "/login") {
        next({ path: "/" });
      }
      // name为超链接
      if (externalLink) {
        openLink(to?.name as string);
        NProgress.done();
      } else {
        // 根据path为二级路由下的页面添加tag
        if (!useMultiTagsStoreHook().getMultiTagsCache) {
          handleAddTagsByPath(to);
        }
        toCorrectRoute();
      }
    } else {
      // 刷新
      if (
        usePermissionStoreHook().wholeMenus.length === 0 &&
        to.path !== "/login"
      )
        initRouter().then((router: Router) => {
          if (!useMultiTagsStoreHook().getMultiTagsCache) {
            handleAddTagsByPath(to);
          }
          router.push(to.fullPath);
        });
      toCorrectRoute();
    }
  } else {
    if (to.path !== "/login") {
      if (whiteList.indexOf(to.path) !== -1) {
        next();
      } else {
        next({ path: "/login" });
      }
    } else {
      next();
    }
  }
});

router.afterEach(() => {
  NProgress.done();
});

export default router;
